import React, { useState, useMemo } from 'react';
import { Flame, Droplets, Mountain, Wind, Trophy, Lock, X, RotateCcw } from 'lucide-react';
import { BADGES, BADGE_TIERS } from '../../utils/badges';

export const BadgeProfile = ({ data, onClose, onResetBadge }) => {
    // Default to 'spark' tier or whichever tier has the most recent activity
    const [activeTier, setActiveTier] = useState('spark');
    
    // Merge user's earned status with static badge definitions
    const userBadges = useMemo(() => {
        return BADGES.map(def => {
            const userState = (data.badges || []).find(b => b.id === def.id);
            return { ...def, ...userState }; // Merges earned: true/false and dates
        });
    }, [data.badges]);

    // Filter by Active Tier
    const displayedBadges = userBadges.filter(b => b.tier === activeTier);
    
    // Calculate Progress for Active Tier
    const tierEarned = displayedBadges.filter(b => b.earned).length;
    const tierTotal = displayedBadges.length;
    const tierProgress = tierTotal > 0 ? (tierEarned / tierTotal) * 100 : 0;

    // Helper: Icons
    const getIcon = (element) => {
        switch(element) {
            case 'fire': return <Flame className="w-5 h-5" />;
            case 'water': return <Droplets className="w-5 h-5" />;
            case 'earth': return <Mountain className="w-5 h-5" />;
            case 'air': return <Wind className="w-5 h-5" />;
            default: return <Trophy className="w-5 h-5" />;
        }
    };

    // Helper: Colors
    const getColors = (element, earned) => {
        // --- H3.A FIX: IMPROVED CONTRAST FOR UNEARNED BADGES ---
        if (!earned) return "bg-slate-900 border-slate-800 text-slate-500"; 
        
        switch(element) {
            case 'fire': return "bg-orange-900/20 border-orange-500/50 text-orange-400 shadow-[0_0_15px_rgba(249,115,22,0.1)]";
            case 'water': return "bg-blue-900/20 border-blue-500/50 text-blue-400 shadow-[0_0_15px_rgba(59,130,246,0.1)]";
            case 'earth': return "bg-emerald-900/20 border-emerald-500/50 text-emerald-400 shadow-[0_0_15px_rgba(16,185,129,0.1)]";
            case 'air': return "bg-cyan-900/20 border-cyan-500/50 text-cyan-400 shadow-[0_0_15px_rgba(34,211,238,0.1)]";
            default: return "bg-yellow-900/20 border-yellow-500/50 text-yellow-400 shadow-[0_0_15px_rgba(234,179,8,0.1)]";
        }
    };

    return (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-md z-[150] flex items-center justify-center p-4 animate-fade-in">
            <div className="bg-slate-950 border border-slate-800 w-full max-w-3xl rounded-2xl shadow-2xl flex flex-col max-h-[85vh] overflow-hidden">
                
                {/* Header */}
                <div className="p-6 border-b border-slate-800 flex justify-between items-start bg-slate-900/50">
                    <div>
                        <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                            <Trophy className="w-6 h-6 text-yellow-500" /> Elemental Mastery
                        </h2>
                        <p className="text-slate-400 text-sm mt-1">Evolution from Spark to Apex.</p>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-slate-800 rounded-full transition-colors text-slate-400 hover:text-white">
                        <X className="w-6 h-6" />
                    </button>
                </div>

                {/* Tier Tabs (Swipe-like navigation) */}
                <div className="flex border-b border-slate-800 bg-slate-950">
                    {Object.values(BADGE_TIERS).map(tier => {
                        const Icon = tier.icon;
                        const isActive = activeTier === tier.id;
                        return (
                            <button 
                                key={tier.id}
                                onClick={() => setActiveTier(tier.id)}
                                className={`flex-1 py-4 flex flex-col items-center gap-1 border-b-2 transition-all ${isActive ? `border-${tier.color.split('-')[1]}-500 bg-slate-900` : 'border-transparent text-slate-500 hover:text-slate-300'}`}
                            >
                                <Icon className={`w-5 h-5 ${isActive ? tier.color : 'text-slate-600'}`} />
                                <span className={`text-xs font-bold uppercase tracking-wider ${isActive ? 'text-white' : ''}`}>{tier.label}</span>
                            </button>
                        );
                    })}
                </div>

                {/* Tier Info & Progress */}
                <div className="px-6 py-4 bg-slate-900/30 border-b border-slate-800">
                    <div className="flex justify-between items-end mb-2">
                        <div>
                            <h3 className={`text-sm font-bold ${BADGE_TIERS[activeTier].color}`}>{BADGE_TIERS[activeTier].desc}</h3>
                        </div>
                        <span className="text-xs font-mono text-slate-400">{tierEarned} / {tierTotal} Unlocked</span>
                    </div>
                    <div className="w-full bg-slate-800 h-2 rounded-full overflow-hidden">
                        <div className={`h-full transition-all duration-1000 ease-out ${BADGE_TIERS[activeTier].bg}`} style={{ width: `${tierProgress}%` }}></div>
                    </div>
                </div>

                {/* Badge Grid */}
                <div className="p-6 overflow-y-auto grid grid-cols-1 sm:grid-cols-2 gap-4 bg-slate-950/80">
                    {displayedBadges.map(badge => (
                        <div key={badge.id} className={`group relative p-4 rounded-xl border flex items-center gap-4 transition-all ${getColors(badge.element, badge.earned)}`}>
                            
                            {/* Icon Circle */}
                            <div className={`p-3 rounded-full border shrink-0 ${badge.earned ? 'bg-slate-950/30 border-white/10' : 'bg-slate-900/50 border-slate-700'}`}>
                                {badge.earned ? getIcon(badge.element) : <Lock className="w-5 h-5 text-slate-600" />}
                            </div>

                            {/* Details */}
                            <div className="flex-1 min-w-0">
                                {/* Improved contrast for unearned titles */}
                                <h4 className={`font-bold truncate ${badge.earned ? 'text-white' : 'text-slate-400'}`}>{badge.name}</h4>
                                
                                {/* Improved contrast for unearned description */}
                                <p className={`text-xs mt-0.5 leading-snug ${badge.earned ? 'opacity-70' : 'text-slate-500'}`}>{badge.desc}</p>
                                
                                {badge.earned && badge.earnedDate && (
                                    <p className="text-[9px] mt-2 font-mono opacity-50 uppercase tracking-wider">
                                        {new Date(badge.earnedDate).toLocaleDateString()}
                                    </p>
                                )}
                            </div>

                            {/* Relock / Reset Button (Hidden unless hovered and earned) */}
                            {badge.earned && onResetBadge && (
                                <button 
                                    onClick={(e) => { e.stopPropagation(); onResetBadge(badge.id); }}
                                    className="absolute bottom-2 right-2 p-1.5 text-slate-600 hover:text-red-400 hover:bg-slate-900 rounded opacity-0 group-hover:opacity-100 transition-all"
                                    title="Reset this badge"
                                >
                                    <RotateCcw className="w-3 h-3" />
                                </button>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};